"use client";

import { useEffect, useRef, useState } from "react";

const CHAT_MESSAGES = [
  {
    id: "q1",
    role: "user",
    text: "What was Vertex Pharma's net revenue guidance revision in Q3 2025, and how did it compare to analyst consensus?",
  },
  {
    id: "a1",
    role: "ai",
    text: "Vertex Pharmaceuticals revised its full-year net revenue guidance upward to ",
    highlighted: "$10.8–11.0B",
    textAfter: ", representing a 7.2% increase from prior guidance of $10.0–10.2B. This exceeded Bloomberg consensus estimates by approximately ",
    highlighted2: "340 basis points",
    textAfter2: ", driven primarily by CASGEVY milestone payments and stronger-than-expected CF franchise performance.",
    citations: [
      { label: "VX Q3 2025 Earnings Call", page: "p.4" },
      { label: "10-Q Filing Oct 2025", page: "§MD&A" },
    ],
  },
];

interface FloatingCardProps {
  className?: string;
  animClass?: string;
  children: React.ReactNode;
}

function FloatingCard({ className = "", animClass = "animate-float-a", children }: FloatingCardProps) {
  return (
    <div className={`glass-card p-4 ${animClass} ${className}`}>
      {children}
    </div>
  );
}

export default function HeroSection() {
  const [typedText, setTypedText] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [showCitations, setShowCitations] = useState(false);
  const fullText = CHAT_MESSAGES[0].text;
  const typingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let i = 0;
    const delay = setTimeout(() => {
      typingRef.current = setInterval(() => {
        if (i < fullText.length) {
          setTypedText(fullText.slice(0, i + 1));
          i++;
        } else {
          if (typingRef.current) clearInterval(typingRef.current);
          setTimeout(() => setShowResponse(true), 600);
          setTimeout(() => setShowCitations(true), 1400);
        }
      }, 22);
    }, 1200);

    return () => {
      clearTimeout(delay);
      if (typingRef.current) clearInterval(typingRef.current);
    };
  }, [fullText]);

  return (
    <section
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16"
      style={{ background: "linear-gradient(160deg, #0B0F1A 0%, #0D1526 45%, #0A1222 100%)" }}
    >
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />

      {/* Radial glow top */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 60% at 50% 0%, rgba(15,244,198,0.09), transparent)" }}
      />

      {/* Radial glow right */}
      <div
        className="absolute top-1/3 right-0 w-[500px] h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 60% 60% at 100% 50%, rgba(245,166,35,0.05), transparent)" }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* ── LEFT COLUMN: Copy ── */}
          <div className="flex flex-col gap-6">
            {/* Label */}
            <div className="animate-fade-up" style={{ animationDelay: "0ms", animationFillMode: "both" }}>
              <span className="section-label">
                GenAI Powered Pharma Research
              </span>
            </div>

            {/* Headline */}
            <div className="animate-fade-up" style={{ animationDelay: "80ms", animationFillMode: "both" }}>
              <h1
                className="font-display text-5xl lg:text-6xl xl:text-7xl font-medium leading-[1.0] tracking-tight"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                <span className="text-white">Ask anything.</span>
                <br />
                <span className="gradient-text-hero">
                  Get cited answers.
                </span>
              </h1>
            </div>

            {/* Subhead */}
            <div className="animate-fade-up" style={{ animationDelay: "160ms", animationFillMode: "both" }}>
              <p className="text-[#8892A4] text-lg leading-relaxed max-w-xl font-light">
                Myndlayer connects your pharma document library — earnings calls, SEC filings, clinical data, ESG reports — to an AI-powered research assistant that grounds every answer in verifiable source citations.
              </p>
            </div>

            {/* Proof points */}
            <div
              className="flex flex-wrap gap-3 animate-fade-up"
              style={{ animationDelay: "240ms", animationFillMode: "both" }}
            >
              {[
                { icon: "🏥", text: "Pharma-specific agents" },
                { icon: "🔗", text: "Inline source citations" },
                { icon: "🔒", text: "Multi-tenant isolation" },
              ].map((item) => (
                <span
                  key={item.text}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium text-[#8892A4]"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}
                >
                  <span>{item.icon}</span>
                  {item.text}
                </span>
              ))}
            </div>

            {/* CTA buttons */}
            <div
              className="flex flex-col sm:flex-row gap-3 animate-fade-up"
              style={{ animationDelay: "320ms", animationFillMode: "both" }}
            >
              <a href="https://app.myndlayer.ai/" target="_blank" rel="noopener noreferrer" className="btn-teal text-sm px-6 py-3 flex items-center gap-2 justify-center flex-1">
                Get Access Now
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
              <a
                href="/contact"
                className="text-sm px-6 py-3 flex items-center gap-2 justify-center rounded-lg font-medium transition-all hover:opacity-90 flex-1"
                style={{ border: "1px solid rgba(255,255,255,0.07)", color: "#8892A4", background: "rgba(255,255,255,0.04)" }}
              >
                Contact Sales
              </a>
            </div>

            {/* Social proof mini */}
            <div
              className="flex items-center gap-4 animate-fade-up"
              style={{ animationDelay: "400ms", animationFillMode: "both" }}
            >
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
                  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&q=80",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Myndlayer user ${i + 1}`}
                    className="w-8 h-8 rounded-full object-cover border-2"
                    style={{ borderColor: "#0B0F1A" }}
                  />
                ))}
              </div>
              <p className="text-sm text-[#8892A4]">
                <span className="text-white font-semibold">140+</span> pharma researchers in early access
              </p>
            </div>
          </div>

          {/* ── RIGHT COLUMN: Chat mockup ── */}
          <div className="relative flex items-center justify-center">
            {/* Floating stat card — top left */}
            <FloatingCard
              className="absolute -top-6 -left-4 lg:-left-12 z-20 min-w-[160px] shadow-card"
              animClass="animate-float-b"
            >
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: "rgba(15,244,198,0.15)" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0FF4C6" strokeWidth="2.5">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-white">Citation verified</span>
              </div>
              <p className="text-[10px] text-[#8892A4] font-mono">10-Q § MD&A · p.14</p>
              <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                <div className="h-full rounded-full animate-gradient" style={{ width: "78%", background: "linear-gradient(90deg, #0FF4C6, #38BDF8, #0FF4C6)", backgroundSize: "200% auto" }} />
              </div>
            </FloatingCard>

            {/* Floating doc count card — bottom right */}
            <FloatingCard
              className="absolute -bottom-4 -right-4 lg:-right-8 z-20 min-w-[140px] shadow-card"
              animClass="animate-float-c"
            >
              <div className="text-2xl font-display font-semibold text-white mb-0.5" style={{ fontFamily: "Fraunces, serif" }}>
                2.4M
              </div>
              <div className="text-[11px] text-[#8892A4]">passages indexed</div>
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse" />
                <span className="text-[10px] text-[#0FF4C6] font-medium">Live sync</span>
              </div>
            </FloatingCard>

            {/* Main chat window */}
            <div
              className="w-full max-w-lg rounded-2xl overflow-hidden shadow-card"
              style={{
                background: "#111827",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(15,244,198,0.06)",
              }}
            >
              {/* Window chrome */}
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{ background: "#0F1521", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
              >
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ background: "#FF5F57" }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: "#FFBD2E" }} />
                    <div className="w-3 h-3 rounded-full" style={{ background: "#28C840" }} />
                  </div>
                  <span className="text-xs text-[#4B5568] font-mono ml-2">IR Agent · Vertex Pharmaceuticals workspace</span>
                </div>
                <div className="agent-badge-live text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse inline-block" />
                  Live
                </div>
              </div>

              {/* Chat body */}
              <div className="p-4 space-y-4 min-h-[320px] relative">
                {/* Scan line */}
                <div className="scan-line" />

                {/* User message */}
                <div className="flex justify-end">
                  <div className="chat-bubble-user text-xs leading-relaxed">
                    {typedText}
                    {typedText.length < fullText.length && (
                      <span className="animate-cursor text-[#0FF4C6] ml-0.5">|</span>
                    )}
                  </div>
                </div>

                {/* AI response */}
                {showResponse && (
                  <div
                    className="chat-bubble-ai text-xs leading-relaxed"
                    style={{ animation: "fade-up 0.5s cubic-bezier(0.22,1,0.36,1) forwards" }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-[#0B0F1A]"
                        style={{ background: "linear-gradient(135deg, #0FF4C6, #38BDF8)" }}
                      >
                        M
                      </div>
                      <span className="text-[10px] text-[#4B5568] font-medium">IR Agent · AI Research</span>
                    </div>

                    <p className="text-[#C8D0DC] leading-[1.8]">
                      Vertex Pharmaceuticals revised its full-year net revenue guidance upward to{" "}
                      <span className="gradient-text-teal font-semibold">$10.8–11.0B</span>
                      , representing a 7.2% increase from prior guidance of $10.0–10.2B. This exceeded Bloomberg consensus estimates by approximately{" "}
                      <span className="gradient-text-amber font-semibold">340 basis points</span>
                      , driven primarily by CASGEVY milestone payments and stronger-than-expected CF franchise performance.
                    </p>

                    {/* Citations */}
                    {showCitations && (
                      <div
                        className="mt-3 pt-3 flex flex-wrap gap-2"
                        style={{ borderTop: "1px solid rgba(255,255,255,0.06)", animation: "citation-appear 0.4s ease forwards" }}
                      >
                        <span className="text-[10px] text-[#4B5568] mr-1 self-center">Sources:</span>
                        <span className="source-pill">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          VX Q3 2025 Earnings · p.4
                        </span>
                        <span className="source-pill source-pill-amber">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          10-Q Filing Oct 2025 · §MD&A
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Input bar */}
              <div
                className="px-4 py-3 flex items-center gap-3"
                style={{ background: "#0F1521", borderTop: "1px solid rgba(255,255,255,0.06)" }}
              >
                <div
                  className="flex-1 rounded-lg px-3 py-2 text-xs text-[#4B5568]"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  Ask a follow-up question…
                </div>
                <button
                  className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #0FF4C6, #0BD4A8)" }}
                  aria-label="Send message"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0B0F1A" strokeWidth="2.5">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs text-[#4B5568] tracking-widest uppercase font-medium">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-[#0FF4C6] to-transparent" />
      </div>
    </section>
  );
}